import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaPaperPlane, FaUserFriends, FaUsers, FaDonate, FaWhatsapp, FaEdit, FaTrash, FaSpinner, FaTrashAlt, FaSearch } from 'react-icons/fa';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import StyledDataTable from '../../components/StyledDataTable/StyledDataTable';
import { Form, Button, Alert, Modal, Tabs, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import './wageni.css';
import { guestApi, groupApi } from '../../api/guestList';
import { useAuthContext } from '../../context/AuthContext';
import AddGuestModal from '../../modals/wageniModal/AddGuestModal';
import EditGuestModal from '../../modals/wageniModal/EditGuestModal';
import DeleteGuestModal from '../../modals/wageniModal/DeleteGuestModal';
import BulkImportModal from '../../modals/wageniModal/BulkImportModal';
import BulkDeleteModal from '../../modals/wageniModal/BulkDeleteModal';
import SuccessModal from '../../modals/wageniModal/SuccessModal';
import TumaTaarifaModal from '../../modals/wageniModal/TumaTaarifaModal';
import KumbushaMichangoModal from '../../modals/wageniModal/KumbushaMichangoModal';
import { useDebounce } from '../../hooks/useDebounce';
import { eventApi } from '../../api/events';
import Groups from '../groups/Groups';

const Wageni = ({ isDashboard = false }) => {
  const { currentEvent, isAuthenticated } = useAuthContext();

  // State for data
  const [guests, setGuests] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [activeTab, setActiveTab] = useState('wageni-wote');
  const [selectedGroupFilter, setSelectedGroupFilter] = useState('');

  // UI State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [overallTotalGuests, setOverallTotalGuests] = useState(0);
  const [pledgersCount, setPledgersCount] = useState(0);
  const [contributorsCount, setContributorsCount] = useState(0);
  const perPage = 10;
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showMultipleModal, setShowMultipleModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);

  const [importResult, setImportResult] = useState({ success: [], skipped: [], errors: [] });
  const [showInspireModal, setShowInspireModal] = useState(false);
  const [hasShownInspire, setHasShownInspire] = useState(() => {
    const eventId = currentEvent?.id || 'default';
    return sessionStorage.getItem(`hasShownInspire_${eventId}`) === 'true';
  });
  const [showTumaTaarifaModal, setShowTumaTaarifaModal] = useState(false);
  const [showMichangoModal, setShowMichangoModal] = useState(false);

  // Fetch groups only on mount
  useEffect(() => {
    if (isAuthenticated && currentEvent) {
      fetchGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentEvent]);

  const isFirstRender = useRef(true);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Fetch when debounced search term changes
  useEffect(() => {
    if (!isAuthenticated || !currentEvent) return;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchGuests(1, debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, isAuthenticated, currentEvent]);


  // Fetch guests on mount, page change, or tab change
  useEffect(() => {
    if (isAuthenticated && currentEvent) {
      fetchGuests(currentPage, searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentEvent, currentPage, activeTab]);

  const fetchGuests = useCallback(async (page = 1, search = '') => {
    // Use tableLoading for page changes (keeps DataTable mounted), loading only for initial load
    if (guests.length === 0) {
      setLoading(true);
    }
    setTableLoading(true);
    try {
      // Fetch specified page and search query from database (10 records per page)
      const response = activeTab === 'waalikwa'
        ? await guestApi.getInvitedGuests(page, search)
        : await guestApi.getGuests(page, search);
      // API returns: { success, data: { data: [...], current_page, per_page, total_records, total_pages } }
      const responseData = response?.data || response || {};
      const guestsData = responseData?.data?.data || responseData?.data || responseData || [];

      // Update pagination info from API response (don't set currentPage here to avoid re-triggering useEffect)
      const fetchedTotalRecords = responseData?.data?.total_records || responseData?.total_records || 0;
      setTotalRecords(fetchedTotalRecords);

      // If we are not searching, store the absolute total of guests in the system
      if (search === '') {
        setOverallTotalGuests(fetchedTotalRecords);
        try {
          const statsResponse = await eventApi.getEventStats();
          const statsData = statsResponse?.data?.data || statsResponse?.data || {};
          setPledgersCount(statsData.pledgers || 0);
          setContributorsCount(statsData.contributors || 0);
        } catch (statsErr) {
          console.warn('Failed to fetch event stats:', statsErr);
        }
      }

      console.log(`[Wageni] Loaded page ${page}: ${guestsData.length} records (total: ${response?.data?.total_records || 0})`);

      // Transform data
      const formattedGuests = guestsData.map(guest => {
        const pledgeAmount = guest.pledge || 0;
        const paidAmount = guest.fund || 0;

        const firstGroup = Array.isArray(guest.groups) && guest.groups.length > 0
          ? guest.groups[0].group_name
          : 'No Group';

        return {
          id: guest.id ?? `${guest.card_number || 'guest'}-${guest.phone_number || ''}`,
          pledge_id: null,
          jina: guest.name || guest.full_name || '',
          kikundi: firstGroup,
          groups: guest.groups || [],
          namba: guest.phone_number || guest.mobile || '',
          mchango: `TZS ${(parseFloat(pledgeAmount) || 0).toLocaleString()}`,
          pledge_amount_raw: parseFloat(pledgeAmount) || 0,
          paid_amount_raw: parseFloat(paidAmount) || 0,
          paid_amount: `TZS ${(parseFloat(paidAmount) || 0).toLocaleString()}`,
          card_type: (guest.card_type == 1 || guest.card_type === '1') ? 'Single' :
            (guest.card_type == 2 || guest.card_type === '2') ? 'Double' :
              (guest.card_type || 'Single'),
          email: guest.email,
          rsvp: guest.rsvp,
          ecard_sent: activeTab === 'waalikwa' ? true :
            guest.ecard_sent === true || guest.ecard_sent === 1 || guest.ecard_sent === '1' ||
            guest.card_sent === true || guest.card_sent === 1 || guest.card_sent === '1' ||
            guest.is_ecard_sent === true || guest.is_ecard_sent === 1 || guest.is_ecard_sent === '1',
        };
      });

      setGuests(formattedGuests);

      // Show inspire modal if overall database has less than 5 guests, and we are not currently searching
      if (fetchedTotalRecords < 5 && !hasShownInspire && search === '') {
        setShowInspireModal(true);
        setHasShownInspire(true);
        sessionStorage.setItem(`hasShownInspire_${currentEvent?.id || 'default'}`, 'true');
      }
    } catch (fetchErr) {
      console.error('Error fetching guests:', fetchErr);
      if (fetchErr.response && fetchErr.response.status !== 404) {
        toast.error('Failed to load guests');
      }
      setGuests([]);
      if (!hasShownInspire) {
        setShowInspireModal(true);
        setHasShownInspire(true);
        sessionStorage.setItem(`hasShownInspire_${currentEvent?.id || 'default'}`, 'true');
      }
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }, [activeTab, hasShownInspire, guests.length]);

  const fetchGroups = async () => {
    try {
      const response = await groupApi.getGroups();
      console.log('[Wageni] fetchGroups response:', response);

      // API returns: { success, data: { data: [...] } } or { success, data: [...] }
      let groupsData = response?.data?.data || response?.data || response || [];
      console.log('[Wageni] After data extraction:', groupsData);

      // Handle wrapped array (e.g., [[{...}, {...}]])
      if (Array.isArray(groupsData) && groupsData.length > 0 && Array.isArray(groupsData[0])) {
        console.log('[Wageni] Detected nested array, flattening...');
        groupsData = groupsData.flat();
      }

      if (!Array.isArray(groupsData)) {
        // If it's an object with numbered keys or similar, convert to array
        groupsData = Object.values(groupsData);
      }

      console.log('[Wageni] Final groups data:', groupsData);
      setGroups(Array.isArray(groupsData) ? groupsData : []);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setGroups([]);
    }
  };

  // Open edit modal with guest data
  const openEditModal = (guest) => {
    setSelectedGuest(guest);
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (guest) => {
    setSelectedGuest(guest);
    setShowDeleteModal(true);
  };

  const handleBulkDeleteClick = () => {
    if (!isSelectionMode) {
      setIsSelectionMode(true);
      return;
    }

    if (selectedRows.length === 0) {
      toast.warning('Tafadhali chagua wageni kwanza (Please select guests first)');
      return;
    }
    setShowBulkDeleteModal(true);
  };

  // ―― Column definitions per tab ―――――――――――――――――――――――――――――――――――――――――――――――――
  const actionsCol = {
    name: 'Actions',
    cell: (row) => (
      <div className="d-flex gap-2 align-items-center justify-content-center">
        <Button
          variant="outline-primary"
          size="sm"
          className="p-1 px-2 d-flex align-items-center justify-content-center"
          onClick={() => openEditModal(row)}
          title="Hariri Mgeni"
          style={{ borderRadius: '6px' }}
        >
          <FaEdit size={14} />
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          className="p-1 px-2 d-flex align-items-center justify-content-center"
          onClick={() => openDeleteModal(row)}
          title="Futa Mgeni"
          style={{ borderRadius: '6px' }}
        >
          <FaTrash size={14} />
        </Button>
      </div>
    ),
    width: '95px',
    center: true
  };

  const colIndex = { name: '#', cell: (row, index) => ((currentPage - 1) * perPage + index + 1), width: '50px' };
  const colJina = { name: 'Jina', selector: r => r.jina, sortable: true, grow: 2, wrap: true, minWidth: '150px' };
  const colNamba = { name: 'Namba', selector: r => r.namba, sortable: true, wrap: true, minWidth: '130px' };
  const colKikundi = { name: 'Kikundi', selector: r => r.kikundi, sortable: true, hide: 'sm', minWidth: '110px' };
  const colAhadi = { name: 'Ahadi', selector: r => `TZS ${r.pledge_amount_raw.toLocaleString()}`, sortable: true, minWidth: '120px' };
  const colMchango = { name: 'Mchango', selector: r => `TZS ${r.paid_amount_raw.toLocaleString()}`, sortable: true, minWidth: '120px' };
  const colCard = { name: 'Card Type', selector: r => r.card_type, sortable: true, minWidth: '110px' };


  const columnsByTab = {
    'wageni-wote': [colIndex, colJina, colKikundi, colNamba, colAhadi, colMchango, actionsCol],
    'walio-ahidi': [colIndex, colJina, colNamba, colAhadi, actionsCol],
    'walio-changia': [colIndex, colJina, colNamba, colMchango, actionsCol],
    'waalikwa': [colIndex, colJina, colNamba, colCard, actionsCol],
  };


  const columns = columnsByTab[activeTab] || columnsByTab['wageni-wote'];

  const filteredGroups = useMemo(() => groups.filter(group =>
    group.group_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ), [groups, searchTerm]);

  const getGroupMembersCount = (group) => {
    if (typeof group.member_count === 'number') return group.member_count;
    if (typeof group.members_count === 'number') return group.members_count;
    if (Array.isArray(group.members)) return group.members.length;
    return guests.filter(guest => guest.groups?.some(g => (g.id || g) === group.id)).length;
  };

  const filteredData = guests.filter(guest => {
    // Group filter (only applies on wageni-wote tab)
    if (selectedGroupFilter && activeTab === 'wageni-wote') {
      const guestGroupIds = guest.groups?.map(g => g.id || g) || [];
      if (!guestGroupIds.includes(parseInt(selectedGroupFilter))) return false;
    }

    if (activeTab === 'walio-ahidi') return guest.pledge_amount_raw > 0;
    if (activeTab === 'walio-changia') return guest.paid_amount_raw > 0;
    if (activeTab === 'waalikwa') return guest.ecard_sent === true;
    return true; // 'wageni-wote'
  });

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <FaSpinner className="fa-spin" size={48} color="#4169e1" />
          <p className="mt-3">Loading guests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      {!isDashboard && (
        <h1 className="stylish-heading text-center" style={{ color: '#4169e1' }}>Wageni</h1>
      )}

      {/* Success Toast Messages */}
      {deleteSuccess && (
        <Alert variant="success" className="position-fixed top-0 end-0 m-3" style={{ zIndex: 9999 }} onClose={() => setDeleteSuccess(false)} dismissible>
          Guest deleted successfully!
        </Alert>
      )}
      {updateSuccess && (
        <Alert variant="success" className="position-fixed top-0 end-0 m-3" style={{ zIndex: 9999 }} onClose={() => setUpdateSuccess(false)} dismissible>
          Guest updated successfully!
        </Alert>
      )}

      {/* Tabs at the top */}
      {!isDashboard && (
        <div className="mt-4 mb-4">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3 custom-wageni-tabs"
          >
            <Tab eventKey="wageni-wote" title="Wageni Wote" />
            <Tab eventKey="walio-ahidi" title="Walio Ahidi" />
            <Tab eventKey="walio-changia" title="Walio Changia" />
            <Tab eventKey="waalikwa" title="Waalikwa" />
            <Tab eventKey="vikundi" title="Makundi" />
          </Tabs>

          {/* Per-tab subtitle + count */}
          <div className="tab-subtitle-container px-2">
            {activeTab === 'wageni-wote' && (
              <p className="text-muted small mb-0">
                Wageni wote <strong>{overallTotalGuests}</strong> walioorodheshwa kwenye tukio.
              </p>
            )}
            {activeTab === 'walio-ahidi' && (
              <p className="text-muted small mb-0">
                Wageni <strong>{pledgersCount}</strong> ambao wameahidi kutoa mchango.
              </p>
            )}
            {activeTab === 'walio-changia' && (
              <p className="text-muted small mb-0">
                Wageni <strong>{contributorsCount}</strong> ambao wameshatoa mchango.
              </p>
            )}
            {activeTab === 'waalikwa' && (
              <p className="text-muted small mb-0">
                Wageni <strong>{totalRecords}</strong> ambao wamepata kadi ya mwaliko.
              </p>
            )}
          </div>
        </div>
      )}



      {activeTab !== 'vikundi' && (
        <div className="bg-white p-3 rounded-4 shadow-sm border mb-4 d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-3">
          <div className="position-relative flex-grow-1" style={{ maxWidth: '420px' }}>
            <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" style={{ zIndex: 5 }} />
            <Form.Control
              type="text"
              placeholder="Tafuta kwa jina au namba..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ps-5 shadow-none border"
              style={{ borderRadius: '25px', height: '42px', fontSize: '0.85rem', borderColor: '#e2e8f0' }}
            />
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap flex-sm-nowrap">
            {activeTab === 'wageni-wote' && (
              <>
                <Button variant="primary" className="shadow-sm rounded-pill px-3 fw-medium" onClick={() => setShowModal(true)}>
                  <i className="bi bi-person-plus me-1"></i> Ongeza Mgeni
                </Button>
                <Button variant="outline-primary" className="shadow-sm rounded-pill px-3 fw-medium" onClick={() => setShowMultipleModal(true)}>
                  <i className="bi bi-upload me-1"></i> Import Contact
                </Button>
                {isSelectionMode ? (
                  <>
                    <Button
                      variant="danger"
                      className="shadow-sm rounded-pill px-3 fw-medium"
                      onClick={handleBulkDeleteClick}
                    >
                      <FaTrashAlt className="me-1" />
                      Futa Walioteuliwa {selectedRows.length > 0 && `(${selectedRows.length})`}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      className="shadow-sm rounded-pill px-3 fw-medium"
                      onClick={() => {
                        setIsSelectionMode(false);
                        setSelectedRows([]);
                        setClearSelectedRows(prev => !prev);
                      }}
                    >
                      Ghairi
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline-danger"
                    className="shadow-sm rounded-pill px-3 fw-medium"
                    onClick={handleBulkDeleteClick}
                  >
                    <FaTrashAlt className="me-1" />
                    Futa Kwa Wingi
                  </Button>
                )}
              </>
            )}

            {activeTab === 'walio-ahidi' && (
              <>
                 <Button variant="primary" className="shadow-sm rounded-pill px-3 fw-medium" onClick={() => setShowModal(true)}>
                  <i className="bi bi-person-plus me-1"></i> Ahadi Mpya
                </Button>
                <Button variant="outline-primary" className="shadow-sm rounded-pill px-3 fw-medium" onClick={() => setShowMultipleModal(true)}>
                  <i className="bi bi-upload me-1"></i>Import Ahadi
                </Button>
                <Button variant="outline-warning" className="shadow-sm rounded-pill px-3 fw-medium text-dark" onClick={() => setShowMichangoModal(true)}>
                  <FaDonate className="me-1" /> Kumbushia Michango
                </Button>
              </>
            )}

            {activeTab === 'walio-changia' && (
              <>
                <Link to="/ecards" className="btn btn-outline-success shadow-sm rounded-pill px-3 fw-medium">
                  <FaWhatsapp className="me-1" /> Tuma Mialiko
                </Link>
                <Button variant="outline-primary" className="shadow-sm rounded-pill px-3 fw-medium" onClick={() => setShowModal(true)}>
                  <i className="bi bi-plus-circle me-1"></i> Mchango Mpya
                </Button>
              </>
            )}

            {activeTab === 'waalikwa' && (
              <>
                <Link to="/ecards" className="btn btn-outline-success shadow-sm rounded-pill px-3 fw-medium">
                  <FaWhatsapp className="me-1" /> Tuma Mialiko
                </Link>

              </>
            )}
          </div>
        </div>
      )}

      {/* Data Table or Groups view */}
      {activeTab === 'vikundi' ? (
        <div className="groups-tab-content">
          <Groups />
        </div>
      ) : (
        <StyledDataTable
          columns={columns}
          data={filteredData}
          progressPending={tableLoading}
          paginationServer
          paginationTotalRows={totalRecords}
          paginationPerPage={perPage}
          paginationDefaultPage={currentPage}
          onChangePage={(page) => setCurrentPage(page)}
          paginationRowsPerPageOptions={[]}
          paginationComponentOptions={{
            noRowsPerPage: true,
            rangeSeparatorText: 'ya',
            selectAllRowsItem: false,
          }}
          selectableRows={isSelectionMode}
          onSelectedRowsChange={({ selectedRows: rows }) => setSelectedRows(rows)}
          clearSelectedRows={clearSelectedRows}
          emptyMessage="Hakuna wageni walioorodheshwa bado."
          pageInfo={
            totalRecords > 0
              ? `Ukurasa ${currentPage} kati ya ${Math.ceil(totalRecords / perPage)} - Jumla ya wageni: ${totalRecords}`
              : undefined
          }
        />
      )}

      {/* External Modals */}
      <AddGuestModal
        show={showModal}
        onHide={() => setShowModal(false)}
        groups={groups}
        onGuestAdded={fetchGuests}
      />

      <EditGuestModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        guest={selectedGuest}
        groups={groups}
        onGuestUpdated={fetchGuests}
      />

      <DeleteGuestModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        guest={selectedGuest}
        onGuestDeleted={fetchGuests}
      />

      <BulkImportModal
        show={showMultipleModal}
        onHide={() => setShowMultipleModal(false)}
        onImportComplete={(result) => {
          setImportResult(result);
          setShowSuccessModal(true);
          fetchGuests();
        }}
      />

      <BulkDeleteModal
        show={showBulkDeleteModal}
        onHide={() => setShowBulkDeleteModal(false)}
        selectedRows={selectedRows}
        onBulkDeleteComplete={() => {
          setSelectedRows([]);
          setClearSelectedRows(prev => !prev);
          setIsSelectionMode(false);
          fetchGuests();
        }}
      />

      <SuccessModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        importResult={importResult}
      />

      <TumaTaarifaModal
        show={showTumaTaarifaModal}
        onHide={() => setShowTumaTaarifaModal(false)}
      />

      <KumbushaMichangoModal
        show={showMichangoModal}
        onHide={() => setShowMichangoModal(false)}
      />

      {/* Inspire Modal to encourage adding guests */}


    </div>
  );
};

export default Wageni;
